import { getUrl } from '../../config';
import React, { forwardRef } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { rgba, darken } from "polished";

import { Chip, Collapse, ListItemButton, ListItemText } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";

const CustomRouterLink = forwardRef((props, ref) => (
  <div ref={ref}>
    <NavLink {...props} />
  </div>
));

const Item = styled(ListItemButton)`
  padding-top: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-bottom: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-left: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 14 : 8)};
  padding-right: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 4 : 7)};
  margin: 3px 12px;
  border-radius: 8px;
  font-weight: 500;
  overflow-x: hidden;
  max-width: calc(100% - 24px);
  min-height: 44px;
  transition: all 0.2s ease-in-out;

  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    min-width: 20px;
    opacity: 0.65;
    flex-shrink: 0;
    transition: color 0.2s ease-in-out, opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
  }

  span {
    transition: color 0.2s ease-in-out, font-weight 0.2s ease-in-out;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: ${(props) => props.theme.sidebar.color};

    svg {
      opacity: 0.95;
      transform: translateX(3px);
    }
  }

  &.${(props) => props.activeclassname} {
    background: linear-gradient(90deg, rgba(71, 130, 218, 0.16) 0%, rgba(71, 130, 218, 0.05) 100%);
    color: #fff;
    padding-left: ${(props) =>
      props.theme.spacing(props.depth && props.depth > 0 ? 14 : 8)};
    
    span {
      color: #fff;
      font-weight: 600;
    }
    
    svg {
      color: ${(props) => props.theme.palette.primary.main};
      opacity: 1;
    }
  }

  &.highlight-red {
    background: rgba(255, 77, 79, 0.08);
    padding-left: ${(props) =>
      props.theme.spacing(props.depth && props.depth > 0 ? 14 : 8)};
    
    svg {
      color: #ff4d4f !important;
      opacity: 0.9 !important;
    }
    
    .MuiListItemText-primary {
      color: #ff4d4f !important;
      font-weight: 600 !important;
    }

    &:hover {
      background: rgba(255, 77, 79, 0.15);

      svg {
        transform: translateX(3px);
      }
    }

    &.${(props) => props.activeclassname} {
      background: rgba(255, 77, 79, 0.18);
    }
  }
`;

const Title = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== "forcewhite",
})`
  margin: 0;
  overflow: hidden;
  min-width: 0;
  & > span {
    color: ${(props) =>
    props.forcewhite
      ? props.theme.sidebar.color
      : rgba(
        props.theme.sidebar.color,
        props.depth && props.depth > 0 ? 0.7 : 0.9
      )};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(3)};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    transition: color 0.2s ease-in-out;
  }
`;

const Badge = styled(Chip)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 18px;
  position: absolute;
  right: ${(props) => (props.haschildren === "true" ? "48px" : "16px")};
  top: 13px;
  background: ${(props) => (props.highlightred === "true" ? "#ff4d4f" : props.theme.sidebar.badge.background)};
  z-index: 1;
  border-radius: 9px;
  span.MuiChip-label,
  span.MuiChip-label:hover {
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    cursor: pointer;
    color: #ffffff !important;
    padding-left: 6px;
    padding-right: 6px;
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const SidebarNavListItem = (props) => {
  const {
    title,
    href,
    depth = 0,
    children,
    icon: Icon,
    badge,
    open: openProp = false,
    perm,
    highlightRed,
  } = props;

  const [open, setOpen] = React.useState(openProp);
  const isForcedWhite =
    depth === 1 &&
    (title === "Opportunités et Risques" ||
      title === "Paramètres RH" ||
      title === "Général" ||
      title === "Financier" ||
      title === "Produits" ||
      title === "Système");

  const handleToggle = () => {
    setOpen((state) => !state);
  };

  const { group, user } = useAuth();

  let hasAccess = false;

  if (perm == null) {
    hasAccess = true;
  } else if (perm === "dev_access") {
    hasAccess = user != null && user.id === 14;
  } else if (perm === "isSuperAdmin = 1") {
    hasAccess = user != null && (
      user.is_super_admin === true ||
      user.is_super_admin === 1 ||
      user.isSuperAdmin === true ||
      user.isSuperAdmin === 1
    );
  } else if (perm === "rh_view") {
    const isSuperAdmin = user != null && (
      user.is_super_admin === true ||
      user.is_super_admin === 1 ||
      user.isSuperAdmin === true ||
      user.isSuperAdmin === 1
    );
    if (isSuperAdmin) {
      hasAccess = true;
    } else if (group != null) {
      hasAccess = group.rh_view == 1;
    }
  } else if (perm === "qualite_view") {
    const isSuperAdmin = user != null && (
      user.is_super_admin === true ||
      user.is_super_admin === 1 ||
      user.isSuperAdmin === true ||
      user.isSuperAdmin === 1
    );
    if (isSuperAdmin) {
      hasAccess = true;
    } else if (group != null) {
      hasAccess = group.qualite_view == 1;
    }
  } else if (group != null) {
    hasAccess = group[perm] == 1;
  }

  if (!hasAccess) {
    return null;
  }

  const itemClassName = highlightRed ? "highlight-red" : "";

  if (children) {
    return (
      <React.Fragment>
        <Item depth={depth} onClick={handleToggle} className={itemClassName}>
          {Icon && <Icon />}
          <Title depth={depth} forcewhite={isForcedWhite}>
            {title}
          </Title>
          {badge && <Badge label={badge} haschildren="true" highlightred={highlightRed ? "true" : "false"} />}
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Item>
        <Collapse in={open}>{children}</Collapse>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Item
        depth={depth}
        component={CustomRouterLink}
        to={href}
        activeclassname="active"
        className={itemClassName}
      >
        {Icon && <Icon />}
        <Title depth={depth} forcewhite={isForcedWhite}>
          {title}
        </Title>
        {badge && <Badge label={badge} highlightred={highlightRed ? "true" : "false"} />}
      </Item>
    </React.Fragment>
  );
};

export default SidebarNavListItem;
